import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import HelpHint from '@/components/HelpHint';
import useTrackToolUsage from '@/hooks/useTrackToolUsage';
import { Sparkle, Share, Copy, Heart, TrendUp, Vault, Bank, Calculator, ShieldCheck } from '@phosphor-icons/react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';

const DIMENSION_ICONS = {
  savings_rate: TrendUp,
  debt_levels: Bank,
  emergency_fund: ShieldCheck,
  retirement_preparedness: Vault,
  tax_efficiency: Calculator,
};

const DIMENSION_LABELS = {
  savings_rate: 'Savings Rate',
  debt_levels: 'Debt Levels',
  emergency_fund: 'Emergency Fund',
  retirement_preparedness: 'Retirement',
  tax_efficiency: 'Tax Efficiency',
};

const WealthHealthScore = () => {
  useTrackToolUsage('wealth_health_score');
  const [scoreData, setScoreData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareUrl, setShareUrl] = useState('');
  const [sharing, setSharing] = useState(false);

  useEffect(() => { fetchScore(); }, []);

  const fetchScore = async () => {
    try {
      const response = await api.get('/wealth-health-score');
      setScoreData(response.data);
    } catch (error) {
      toast.error('Failed to load score');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const response = await api.post('/wealth-health-score/share');
      const fullUrl = `${window.location.origin}/share/${response.data.share_token}`;
      setShareUrl(fullUrl);
      toast.success('Share link created!');
    } catch (error) {
      toast.error('Failed to create share link');
    } finally {
      setSharing(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard!');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96 text-[#4A5568]">Calculating your score...</div>;
  }

  if (!scoreData) return null;

  const radarData = Object.entries(scoreData.breakdown).map(([key, val]) => ({
    dimension: DIMENSION_LABELS[key],
    score: val.score,
    fullMark: val.max
  }));

  const gradeColor = scoreData.total_score >= 80 ? '#2D6A4F' : scoreData.total_score >= 60 ? '#C5A059' : scoreData.total_score >= 40 ? '#B7791F' : '#C53030';

  return (
    <div className="space-y-8 fade-in">
      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4A5568]">
            <strong className="text-[#1A202C]">Wealth Health Score™:</strong> Educational composite score (0-100) based on five financial dimensions. This is not financial advice — consult qualified professionals for personalized guidance.
          </p>
        </div>
      </div>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">
            Wealth Health Score™
          </h1>
          <p className="text-base text-[#4A5568]">Your comprehensive financial wellness rating</p>
        </div>
        <Button onClick={handleShare} disabled={sharing} data-testid="share-score-button" className="bg-[#C5A059] hover:bg-[#D4AF37] text-[#0A1128]">
          <Share size={18} className="mr-2" />
          {sharing ? 'Creating Link...' : 'Share Score'}
        </Button>
      </div>

      {shareUrl && (
        <div className="bg-white border border-[#C5A059] rounded-md p-4" data-testid="share-link-section">
          <div className="flex items-center gap-3">
            <Heart size={20} weight="duotone" className="text-[#C5A059]" />
            <div className="flex-1">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-1">Your Share Link</p>
              <p className="text-sm text-[#1A202C] font-mono break-all">{shareUrl}</p>
            </div>
            <Button onClick={handleCopy} data-testid="copy-share-link" variant="outline" className="border-[#C5A059] text-[#0A1128]">
              <Copy size={16} className="mr-2" />Copy
            </Button>
          </div>
        </div>
      )}

      {/* Main Score */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-8 dashboard-card text-center" data-testid="main-score-card">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-4">Overall Score</p>
          <div className="relative inline-block">
            <svg className="transform -rotate-90 w-48 h-48">
              <circle cx="96" cy="96" r="84" stroke="#E2E8F0" strokeWidth="12" fill="none" />
              <circle cx="96" cy="96" r="84" stroke={gradeColor} strokeWidth="12" fill="none"
                strokeDasharray={`${2 * Math.PI * 84}`}
                strokeDashoffset={`${2 * Math.PI * 84 * (1 - scoreData.total_score / 100)}`}
                strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-6xl font-semibold text-[#0A1128] ibm-plex">{scoreData.total_score}</span>
              <span className="text-xl font-medium" style={{ color: gradeColor }}>{scoreData.letter_grade}</span>
            </div>
          </div>
          <p className="text-sm text-[#4A5568] mt-4">out of 100</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="radar-chart">
          <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Dimension Analysis</h2>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#E2E8F0" />
              <PolarAngleAxis dataKey="dimension" style={{ fontSize: '11px', fill: '#4A5568' }} />
              <PolarRadiusAxis domain={[0, 20]} style={{ fontSize: '10px' }} />
              <Radar name="Score" dataKey="score" stroke="#C5A059" fill="#C5A059" fillOpacity={0.4} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Dimension Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {Object.entries(scoreData.breakdown).map(([key, val]) => {
          const Icon = DIMENSION_ICONS[key];
          const isMissing = val.is_missing_data;
          const dimColor = isMissing ? '#A0AABF' : val.score >= 16 ? '#2D6A4F' : val.score >= 10 ? '#C5A059' : val.score >= 5 ? '#B7791F' : '#C53030';
          return (
            <div key={key} className="bg-white border border-slate-200 rounded-md shadow-sm p-5 dashboard-card" data-testid={`dimension-${key}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="p-2 rounded-md" style={{ backgroundColor: `${dimColor}15` }}>
                  <Icon size={22} weight="duotone" style={{ color: dimColor }} />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-[#0A1128] text-sm">{DIMENSION_LABELS[key]}</h3>
                  <p className="text-xs text-[#4A5568]">{val.description}</p>
                </div>
              </div>
              {isMissing ? (
                <div data-testid={`dimension-${key}-missing-data`} className="bg-[#FDFCFB] border border-dashed border-slate-300 rounded-md p-3 mt-2">
                  <p className="text-xs font-semibold text-[#4A5568] uppercase tracking-[0.08em] mb-1">Additional information required</p>
                  <p className="text-xs text-[#4A5568] leading-relaxed">{val.required_inputs}</p>
                </div>
              ) : (
                <>
                  <div className="flex items-end justify-between mb-2">
                    <span className="text-3xl font-semibold ibm-plex" style={{ color: dimColor }}>{val.score}</span>
                    <span className="text-sm text-[#4A5568]">/ {val.max}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden mb-2">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(val.score / val.max) * 100}%`, backgroundColor: dimColor }} />
                  </div>
                  <p className="text-xs text-[#4A5568] font-mono">{val.metric}</p>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Methodology Note */}
      <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
        <h3 className="text-sm font-semibold text-[#1A202C] mb-2">How It's Calculated</h3>
        <p className="text-xs text-[#4A5568] leading-relaxed">
          Your Wealth Health Score™ evaluates five core dimensions (20 points each): Savings Rate (% of income saved), Debt Levels (liabilities vs. income), Emergency Fund (months of expenses covered), Retirement Preparedness (savings vs. age-appropriate target), and Tax Efficiency (diversification across taxable/tax-deferred/tax-free buckets). The composite score (0–100) is for educational purposes only. Add data via Spending Tracker, Income Sources, Retirement Planner, Savings Planner, and Net Worth Tracking to refine your score.
        </p>
      </div>
    </div>
  );
};

export default WealthHealthScore;
